import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updateProfile,
  onAuthStateChanged,
} from 'firebase/auth'
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore'
import { auth, googleProvider, db } from '../config/firebase'

/**
 * Sign up with email and password
 */
export async function signUpWithEmail(email, password, displayName = '') {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password)
    const user = userCredential.user

    // Update display name if provided
    if (displayName) {
      await updateProfile(user, { displayName })
    }

    // Create user document in Firestore
    await createUserDocument(user)

    return { user, error: null }
  } catch (error) {
    return { user: null, error: getAuthErrorMessage(error.code) }
  }
}

/**
 * Sign in with email and password
 */
export async function signInWithEmail(email, password) {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password)
    return { user: userCredential.user, error: null }
  } catch (error) {
    return { user: null, error: getAuthErrorMessage(error.code) }
  }
}

/**
 * Sign in with Google
 */
export async function signInWithGoogle() {
  try {
    const result = await signInWithPopup(auth, googleProvider)
    const user = result.user

    // Check if user document exists, create if not
    const userDoc = await getDoc(doc(db, 'users', user.uid))
    if (!userDoc.exists()) {
      await createUserDocument(user)
    }

    return { user, error: null }
  } catch (error) {
    return { user: null, error: getAuthErrorMessage(error.code) }
  }
}

/**
 * Sign out
 */
export async function signOut() {
  try {
    await firebaseSignOut(auth)
    return { error: null }
  } catch (error) {
    return { error: getAuthErrorMessage(error.code) }
  }
}

/**
 * Send password reset email
 */
export async function resetPassword(email) {
  try {
    await sendPasswordResetEmail(auth, email)
    return { error: null }
  } catch (error) {
    return { error: getAuthErrorMessage(error.code) }
  }
}

/**
 * Create user document in Firestore
 */
async function createUserDocument(user) {
  const userRef = doc(db, 'users', user.uid)
  
  const userData = {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName || '',
    photoURL: user.photoURL || '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }

  // Also create the monetization subdocument with initial values
  const monetizationRef = doc(db, 'users', user.uid, 'monetization', 'data')
  const monetizationData = {
    plan: 'free',
    planSource: null,
    stripeCustomerId: null,
    subscriptionId: null,
    subscriptionStartDate: null,
    subscriptionRenewalDate: null,
    creditsBalance: 0,
    creditsAllotedMonthly: 0,
    creditsUsedThisMonth: 0,
    creditsPurchased: 0,
    usageHistory: [],
    overrideBy: null,
    overrideNote: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }

  await Promise.all([
    setDoc(userRef, userData),
    setDoc(monetizationRef, monetizationData),
  ])
}

/**
 * Subscribe to auth state changes
 */
export function subscribeToAuthState(callback) {
  // Firebase init is non-fatal (see config/firebase.js). With no auth
  // instance, report "signed out" so the app still finishes loading.
  if (!auth) {
    callback(null)
    return () => {}
  }

  return onAuthStateChanged(auth, callback)
}

/**
 * Get user-friendly error message
 */
function getAuthErrorMessage(errorCode) {
  const errorMessages = {
    'auth/email-already-in-use': 'An account with this email already exists.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/operation-not-allowed': 'This sign-in method is not enabled.',
    'auth/weak-password': 'Password should be at least 6 characters.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/invalid-credential': 'Invalid email or password.',
    'auth/too-many-requests': 'Too many attempts. Please try again later.',
    'auth/popup-closed-by-user': 'Sign-in popup was closed.',
    'auth/cancelled-popup-request': 'Sign-in was cancelled.',
    'auth/popup-blocked': 'Sign-in popup was blocked by the browser.',
    'auth/network-request-failed': 'Network error. Please check your connection.',
  }

  return errorMessages[errorCode] || 'An error occurred. Please try again.'
}
